import React, { useState } from 'react';
import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// ─── Anthropic API ────────────────────────────────────────────────────────────

const callClaude = async (prompt) => {
  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1000,
      system: 'You are a helpful business dashboard assistant for MyCouponBook, a coupon and loyalty platform. Give concise, actionable insights in 2-3 sentences.',
      messages: [{ role: 'user', content: prompt }],
    }),
  });
  const data = await res.json();
  return data.content?.map(b => b.text || '').join('') || '';
};

// ─── Mock Data ────────────────────────────────────────────────────────────────

const REDEMPTIONS_DATA = [
  { name: 'JAN', amountOff: 700,  pointsBack: 700  },
  { name: 'FEB', amountOff: 2100, pointsBack: 1400 },
  { name: 'MAR', amountOff: 1800, pointsBack: 2100 },
  { name: 'APR', amountOff: 2800, pointsBack: 2100 },
  { name: 'MAY', amountOff: 1400, pointsBack: 1400 },
  { name: 'JUN', amountOff: 1600, pointsBack: 1400 },
];

const POINTS_DOLLARS_DATA = [
  { name: 'JAN', points: 8,   dollars: 8  },
  { name: 'FEB', points: 19,  dollars: 40 },
  { name: 'MAR', points: 9.2, dollars: 30 },
  { name: 'APR', points: 14,  dollars: 30 },
  { name: 'MAY', points: 8,   dollars: 20 },
  { name: 'JUN', points: 10,  dollars: 20 },
];

const POINTS_REQUESTS = [
  { id: 1, first_name: 'Lan',    date_and_time: 'Jan - 12, 13:30', points: 12, status: 'Expired' },
  { id: 2, first_name: 'Moris',  date_and_time: 'Feb - 06, 05:23', points: 4,  status: 'Done'    },
  { id: 3, first_name: 'John',   date_and_time: 'Feb - 11, 12:20', points: 7,  status: 'Done'    },
  { id: 4, first_name: 'Freddy', date_and_time: 'Feb - 12, 02:12', points: 10, status: 'Active'  },
  { id: 5, first_name: 'Freddy', date_and_time: 'Feb - 12, 02:12', points: 10, status: 'Active'  },
  { id: 6, first_name: 'Freddy', date_and_time: 'Feb - 12, 02:12', points: 10, status: 'Active'  },
  { id: 7, first_name: 'Freddy', date_and_time: 'Feb - 12, 02:12', points: 10, status: 'Active'  },
  { id: 8, first_name: 'Freddy', date_and_time: 'Feb - 12, 02:12', points: 10, status: 'Active'  },
  { id: 9, first_name: 'Freddy', date_and_time: 'Feb - 12, 02:12', points: 10, status: 'Active'  },
];

const ACTIONS = [
  { id: 1, initials: 'PW', name: 'Peter White', msg: 'wants to use the coupon and pay by Dollars.', hasAvatar: true  },
  { id: 2, initials: null, name: null,           msg: 'Profile Have to complete !',                  hasAvatar: false },
  { id: 3, initials: 'NO', name: 'Norman',       msg: 'wants to use the coupon and pay by Dollars.', hasAvatar: true  },
  { id: 4, initials: null, name: null,           msg: 'Profile Have to complete !',                  hasAvatar: false },
];

const COUPONS = [
  { id: 1, title: 'Take a Cup',   state: 'PUBLISHED', expiry_date: '2025-01-01', save_coupon_count: 145, views: 205 },
  { id: 2, title: 'Take a Cup',   state: 'PUBLISHED', expiry_date: '2025-01-01', save_coupon_count: 145, views: 205 },
  { id: 3, title: 'Take a Cup',   state: 'PUBLISHED', expiry_date: '2025-01-01', save_coupon_count: 145, views: 205 },
  { id: 4, title: 'Take a Cup',   state: 'PUBLISHED', expiry_date: '2025-01-01', save_coupon_count: 145, views: 205 },
  { id: 5, title: 'Take a Cup',   state: 'PUBLISHED', expiry_date: '2025-01-01', save_coupon_count: 145, views: 205 },
  { id: 6, title: 'Take a Cup',   state: 'PUBLISHED', expiry_date: '2025-01-01', save_coupon_count: 145, views: 205 },
  { id: 7, title: 'Coffee Deal',  state: 'DRAFT',     expiry_date: '2025-03-01', save_coupon_count: 30,  views: 80  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const card = {
  background: '#fff',
  borderRadius: 16,
  border: '1px solid #ececec',
  boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
};

const statusStyle = (s) => {
  const l = (s || '').toLowerCase();
  if (l === 'expired') return { background: '#fff1f0', color: '#cf1322' };
  if (l === 'done')    return { background: '#f6ffed', color: '#389e0d' };
  if (l === 'active')  return { background: '#e6f7ff', color: '#096dd9' };
  if (l === 'pending') return { background: '#fffbe6', color: '#d48806' };
  return { background: '#f5f5f5', color: '#595959' };
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 14px', fontSize: 12, boxShadow: '0 4px 14px rgba(0,0,0,0.09)' }}>
      <p style={{ margin: '0 0 4px', fontWeight: 700, color: '#1e293b' }}>{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ margin: '2px 0', color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ─── NavBar ───────────────────────────────────────────────────────────────────

function NavBar({ activeNav, setActiveNav }) {
  const items = ['Dashboard', 'Admin Panel', 'My Coupons', 'Referrals', 'Points', 'Plans'];
  const icons = { Dashboard: '⊞', 'Admin Panel': '👤', 'My Coupons': '🏷️', Referrals: '👥', Points: '🎁', Plans: '📋' };

  return (
    <nav style={{
      display: 'flex', alignItems: 'center', gap: 4,
      padding: '10px 28px', background: '#fff',
      borderBottom: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      position: 'sticky', top: 0, zIndex: 200,
    }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginRight: 18, background: '#e53935', borderRadius: 10, padding: '7px 13px', cursor: 'pointer' }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 11 }}>my</span>
        <span style={{ color: '#fff', fontWeight: 900, fontSize: 13, letterSpacing: 0.5 }}>COUPONBOOK</span>
        <div style={{ width: 15, height: 15, background: 'rgba(255,255,255,0.25)', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#fff', fontSize: 8 }}>✂</span>
        </div>
      </div>

      {items.map(item => (
        <button key={item} onClick={() => setActiveNav(item)} style={{
          padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
          fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 5,
          background: activeNav === item ? '#e53935' : 'transparent',
          color: activeNav === item ? '#fff' : '#64748b',
          transition: 'all 0.15s',
        }}>
          <span style={{ fontSize: 12 }}>{icons[item]}</span>{item}
        </button>
      ))}

      <div style={{ flex: 1 }} />

      <button style={{ padding: '8px 18px', borderRadius: 20, border: '2px solid #e53935', background: 'transparent', color: '#e53935', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginRight: 4 }}>
        <span>🎟️</span> Create Coupon
      </button>
      <button style={{ padding: '7px 14px', borderRadius: 20, border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', fontSize: 13 }}>⚙️ Settings</button>
      <div style={{ position: 'relative', marginLeft: 4 }}>
        <button style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔔</button>
        <span style={{ position: 'absolute', top: 2, right: 2, width: 9, height: 9, background: '#e53935', borderRadius: '50%', border: '2px solid #fff' }} />
      </div>
    </nav>
  );
}

// ─── Store Card ───────────────────────────────────────────────────────────────

function StoreCard() {
  return (
    <div style={{ borderRadius: 16, overflow: 'hidden', position: 'relative', height: 296, marginBottom: 16, boxShadow: '0 4px 18px rgba(0,0,0,0.12)' }}>
      <div style={{ width: '100%', height: '100%', background: 'linear-gradient(155deg,#ffd54f 0%,#ffb74d 28%,#81c784 58%,#2e7d32 100%)', position: 'relative' }}>
        <span style={{ position: 'absolute', top: 16, left: '42%', fontSize: 66 }}>🌼</span>
        <span style={{ position: 'absolute', top: 10, right: 24, fontSize: 44 }}>🌸</span>
        <span style={{ position: 'absolute', top: 60, left: 16, fontSize: 36 }}>🌺</span>
        <span style={{ position: 'absolute', bottom: 60, left: 28, fontSize: 40 }}>🏪</span>
      </div>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.05) 55%, transparent 100%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 16px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>☕</div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>Mannyo</div>
            <div style={{ color: 'rgba(255,255,255,0.72)', fontSize: 12 }}>Take A Break cafe</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#69f0ae', fontWeight: 800, fontSize: 22 }}>12% ↗</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 1 }}>Profit</div>
        </div>
      </div>
    </div>
  );
}

// ─── Coupons Sidebar ──────────────────────────────────────────────────────────

function CouponsSidebar() {
  const [filter, setFilter] = useState('PUBLISHED');
  const published = COUPONS.filter(c => c.state === 'PUBLISHED');
  const draft     = COUPONS.filter(c => c.state === 'DRAFT');
  const list      = filter === 'PUBLISHED' ? published : draft;

  return (
    <div style={{ ...card, padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 20 }}>🏷️</span>
          <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>Coupons</span>
        </div>
        <button style={{ width: 30, height: 30, borderRadius: '50%', background: '#e53935', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: 2 }}>+</button>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 12 }}>
        <span style={{ fontSize: 36, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>{String(list.length).padStart(2, '0')}</span>
        <span style={{ fontSize: 24, color: '#cbd5e1', fontWeight: 300, margin: '0 2px' }}>/</span>
        <span style={{ fontSize: 22, color: '#94a3b8', fontWeight: 500 }}>{String(COUPONS.length).padStart(2, '0')}</span>
        <div style={{ marginLeft: 8, display: 'flex', gap: 5 }}>
          <button style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}>⚙</button>
          <button style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}>↗</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
        {[['PUBLISHED','🌐','Published'],['DRAFT','📝','Unpublished']].map(([val, icon, label]) => (
          <button key={val} onClick={() => setFilter(val)} style={{
            padding: '7px 4px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            background: filter === val ? '#e53935' : '#f8fafc',
            color: filter === val ? '#fff' : '#64748b',
            transition: 'all 0.15s',
          }}>
            <span>{icon}</span>{label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: 490, overflowY: 'auto' }}>
        {list.map(coupon => (
          <div key={coupon.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', background: '#f8fafc', borderRadius: 10, border: '1px solid #f0f0f0' }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#e53935', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{coupon.title}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                Expire: {coupon.expiry_date ? new Date(coupon.expiry_date).toLocaleDateString('en-US', { month: 'short', day: '2-digit' }) : 'No expiry'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 11 }}>❤️</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{coupon.save_coupon_count}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 11 }}>👁</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{coupon.views}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Feature Banner ───────────────────────────────────────────────────────────

function FeatureBanner() {
  return (
    <div style={{
      background: 'linear-gradient(120deg,#f5ecca 0%,#e8d97a 40%,#d4bc4a 100%)',
      borderRadius: 18, overflow: 'hidden', position: 'relative',
      minHeight: 150, display: 'flex', alignItems: 'stretch',
      boxShadow: '0 4px 20px rgba(180,150,30,0.18)',
    }}>
      {/* Scattered coins (absolute positioned) */}
      <span style={{ position: 'absolute', top: 8,  left: '34%',  fontSize: 36, transform: 'rotate(-20deg)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.18))' }}>🪙</span>
      <span style={{ position: 'absolute', top: 60, left: '38%',  fontSize: 28, transform: 'rotate(10deg)',  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}>🪙</span>
      <span style={{ position: 'absolute', bottom: 10, left: '32%', fontSize: 22, transform: 'rotate(-5deg)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))' }}>🪙</span>
      <span style={{ position: 'absolute', top: 12, right: '22%',  fontSize: 32, transform: 'rotate(15deg)',  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.18))' }}>🪙</span>
      <span style={{ position: 'absolute', bottom: 18, right: '18%', fontSize: 26, transform: 'rotate(-12deg)', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}>🪙</span>
      <span style={{ position: 'absolute', top: 5,  right: '10%',  fontSize: 20, transform: 'rotate(8deg)',   filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))' }}>🪙</span>

      {/* Left: text content */}
      <div style={{ padding: '28px 32px', zIndex: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1 }}>
        <p style={{ margin: '0 0 4px', fontSize: 16, color: '#5a4a10', fontWeight: 400, letterSpacing: 0.1 }}>New Feature Launch.</p>
        <p style={{ margin: '0 0 18px', fontSize: 26, fontWeight: 900, color: '#1a1200', letterSpacing: '-0.3px', lineHeight: 1.15 }}>Boost your Coupon Reach!</p>
        <div>
          <button style={{
            background: 'transparent', color: '#c62828', border: '1.5px solid #c62828',
            borderRadius: 22, padding: '7px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'inline-block',
          }}>Learn more.</button>
        </div>
      </div>

      {/* Right: people photo placeholder */}
      <div style={{
        width: 320, flexShrink: 0, position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'linear-gradient(to left, rgba(240,220,120,0.6), transparent)',
      }}>
        {/* Two people silhouette */}
        <div style={{ display: 'flex', gap: -20, alignItems: 'flex-end', paddingBottom: 0, zIndex: 2 }}>
          <span style={{ fontSize: 100, lineHeight: 1, filter: 'drop-shadow(-4px 0 8px rgba(0,0,0,0.1))' }}>👩</span>
          <span style={{ fontSize: 90, lineHeight: 1, marginLeft: -20, filter: 'drop-shadow(-4px 0 8px rgba(0,0,0,0.1))' }}>👩‍💼</span>
        </div>
        {/* Slide dots on the right edge */}
        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {[1,0,0].map((active, i) => (
            <div key={i} style={{ width: active ? 8 : 6, height: active ? 8 : 6, borderRadius: '50%', background: active ? '#fff' : 'rgba(255,255,255,0.5)' }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Redemptions Chart ────────────────────────────────────────────────────────

function RedemptionsChart() {
  const [period, setPeriod] = useState('Last 30 Days');
  return (
    <div style={{ ...card, padding: '20px 22px', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <select style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569', background: '#fff', cursor: 'pointer', fontWeight: 500 }}>
          <option>📅 Redemptions</option>
        </select>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569', background: '#fff', cursor: 'pointer' }}>
          <option>Last 30 Days</option>
          <option>Last 7 Days</option>
          <option>Last Year</option>
          <option>Total</option>
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 9, height: 9, background: '#e53935', borderRadius: '50%' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Amount off</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 9, height: 9, background: '#fbc02d', borderRadius: '50%' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Points Back</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'baseline' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Amount Off <strong style={{ color: '#1e293b', fontSize: 15 }}>26</strong></span>
          <span style={{ fontSize: 12, color: '#64748b' }}>Points Back <strong style={{ color: '#1e293b', fontSize: 15 }}>16</strong></span>
          <span style={{ fontSize: 12, color: '#64748b' }}>Total <strong style={{ color: '#1e293b', fontSize: 15 }}>16</strong></span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={REDEMPTIONS_DATA} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} ticks={[0, 700, 1400, 2100, 2800]} />
          <Tooltip content={<ChartTooltip />} />
          <Line type="monotone" dataKey="amountOff"  name="Amount Off"  stroke="#e53935" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: '#e53935', stroke: '#fff', strokeWidth: 2 }} />
          <Line type="monotone" dataKey="pointsBack" name="Points Back" stroke="#fbc02d" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: '#fbc02d', stroke: '#fff', strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Points & Dollars Chart ───────────────────────────────────────────────────

function PointsDollarsChart() {
  const [period, setPeriod] = useState('Last 30 Days');
  return (
    <div style={{ ...card, padding: '20px 22px', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <select style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569', background: '#fff', cursor: 'pointer', fontWeight: 500 }}>
          <option>📅 Redemptions</option>
        </select>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding: '6px 12px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 12, color: '#475569', background: '#fff', cursor: 'pointer' }}>
          <option>Last 30 Days</option>
          <option>Last 7 Days</option>
          <option>Total</option>
        </select>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 9, height: 9, background: '#e53935', borderRadius: '50%' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Points</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 9, height: 9, background: '#fbc02d', borderRadius: '50%' }} />
          <span style={{ fontSize: 12, color: '#64748b' }}>Dollars</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'baseline' }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>Points <strong style={{ color: '#1e293b', fontSize: 15 }}>19.12</strong></span>
          <span style={{ fontSize: 12, color: '#64748b' }}>Dollars <strong style={{ color: '#1e293b', fontSize: 15 }}>2070.78</strong></span>
          <span style={{ fontSize: 12, color: '#64748b' }}>Total <strong style={{ color: '#1e293b', fontSize: 15 }}>16</strong></span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={POINTS_DOLLARS_DATA} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} ticks={[0, 10, 20, 30, 40]} />
          <Tooltip content={<ChartTooltip />} />
          <Line type="monotone" dataKey="dollars" name="Dollars" stroke="#fbc02d" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: '#fbc02d', stroke: '#fff', strokeWidth: 2 }} />
          <Line type="monotone" dataKey="points"  name="Points"  stroke="#e53935" strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: '#e53935', stroke: '#fff', strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Point Requests Table ─────────────────────────────────────────────────────

function PointRequestsTable() {
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading]     = useState(false);

  const handleAI = async () => {
    setLoading(true);
    try {
      const text = await callClaude(
        `Summarize these point requests for my coupon business in 2 sentences: ${POINTS_REQUESTS.map(r => `${r.first_name} ${r.points}pts (${r.status})`).join(', ')}`
      );
      setAiSummary(text);
    } catch {
      setAiSummary('Unable to load summary.');
    }
    setLoading(false);
  };

  return (
    <div style={{ ...card, overflow: 'hidden', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 22px', borderBottom: '1px solid #f8fafc' }}>
        <select style={{ padding: '7px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, color: '#475569', background: '#fff', cursor: 'pointer', fontWeight: 500 }}>
          <option>📅 Point Requests</option>
        </select>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleAI} disabled={loading} style={{ padding: '7px 14px', background: '#fff5f5', color: '#e53935', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {loading ? '…' : '✨ AI Summary'}
          </button>
          <button style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↗</button>
        </div>
      </div>
      {aiSummary && (
        <div style={{ padding: '10px 22px', background: '#fff5f5', borderBottom: '1px solid #fecaca', fontSize: 13, color: '#7f1d1d', lineHeight: 1.55 }}>
          {aiSummary}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['First name', 'Date and time', 'Points', 'Status'].map(h => (
              <th key={h} style={{ padding: '11px 22px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#94a3b8', borderBottom: '1px solid #f8fafc', letterSpacing: 0.2 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {POINTS_REQUESTS.map((r, i) => {
            const ss = statusStyle(r.status);
            return (
              <tr key={r.id} style={{ borderBottom: '1px solid #f8fafc', background: i % 2 === 0 ? '#fff' : '#fdfdfd' }}>
                <td style={{ padding: '13px 22px', fontSize: 14, color: '#1e293b', fontWeight: 500 }}>{r.first_name}</td>
                <td style={{ padding: '13px 22px', fontSize: 13, color: '#64748b' }}>{r.date_and_time}</td>
                <td style={{ padding: '13px 22px', fontSize: 14, color: '#1e293b', fontWeight: 500 }}>{r.points}</td>
                <td style={{ padding: '13px 22px' }}>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, ...ss }}>{r.status}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Premium Card ─────────────────────────────────────────────────────────────

function PremiumCard() {
  return (
    <div style={{
      background: 'linear-gradient(135deg,#ef5350 0%,#c62828 55%,#b71c1c 100%)',
      borderRadius: 18, padding: '20px 22px', color: '#fff',
      position: 'relative', overflow: 'hidden', marginBottom: 12,
      boxShadow: '0 6px 24px rgba(229,57,53,0.38)',
    }}>
      {/* Decorative */}
      <div style={{ position: 'absolute', top: -24, left: -24, width: 110, height: 110, background: 'rgba(255,255,255,0.07)', borderRadius: '50%' }} />
      <div style={{ position: 'absolute', bottom: -30, right: 50, width: 80, height: 80, background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />

      {/* Crown + QR */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, marginBottom: 4 }}>👑</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: '#fdd835', letterSpacing: 0.5 }}>Premium</div>
        </div>
        {/* QR grid */}
        <div style={{ background: '#fff', borderRadius: 10, padding: 7, width: 66, height: 66 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1.5 }}>
            {[1,1,1,0,1,1,1, 1,0,1,0,1,0,1, 1,1,1,0,1,1,1, 0,0,0,0,0,0,0, 1,0,0,1,0,1,0, 0,1,1,0,1,0,1, 1,0,1,1,0,1,1].map((v, i) => (
              <div key={i} style={{ width: '100%', paddingTop: '100%', background: v ? '#1a1a1a' : '#fff', borderRadius: 1 }} />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.18)', paddingTop: 14 }}>
        <div>
          <div style={{ fontSize: 11, color: 'rgba(255,210,120,0.85)', marginBottom: 3 }}>Business name</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>Loading...</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,210,120,0.85)', marginBottom: 3 }}>Points</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14, fontWeight: 700 }}>—</span>
            <span style={{ fontSize: 18, cursor: 'pointer' }}>👁</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Actions Panel ────────────────────────────────────────────────────────────

function ActionsPanel() {
  const [insight, setInsight] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInsight = async () => {
    setLoading(true);
    try {
      const text = await callClaude(
        'I have these pending business actions on my coupon platform: Peter White wants to use a coupon and pay by Dollars, Norman wants to use a coupon and pay by Dollars, and 2 profiles are incomplete. Give me a 2-sentence actionable business recommendation.'
      );
      setInsight(text);
    } catch {
      setInsight('Unable to load AI insight right now. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{ ...card, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* 4-dot grid icon — matches screenshot */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3.5 }}>
            {[0,1,2,3].map(i => <div key={i} style={{ width: 7, height: 7, background: '#e53935', borderRadius: 2 }} />)}
          </div>
          <span style={{ fontWeight: 700, fontSize: 17, color: '#1e293b' }}>Actions</span>
        </div>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, color: '#94a3b8', padding: 0 }}>↗</button>
      </div>

      {/* Action list */}
      <div style={{ padding: '0 10px 10px' }}>
        {ACTIONS.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 8px', borderBottom: '1px solid #f8fafc' }}>
            {a.hasAvatar ? (
              <div style={{
                width: 38, height: 38, borderRadius: '50%', background: '#fce4e4',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                fontSize: 12, fontWeight: 700, color: '#e53935', letterSpacing: 0.5,
              }}>{a.initials}</div>
            ) : (
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18, color: '#94a3b8' }}>👤</div>
            )}
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.45 }}>
              {a.name && <strong style={{ color: '#1e293b' }}>{a.name} </strong>}
              {a.msg}
            </p>
          </div>
        ))}
      </div>

      {/* AI insight text */}
      {insight && (
        <div style={{ margin: '0 12px 10px', padding: '10px 12px', background: '#fff5f5', borderRadius: 10, border: '1px solid #fecaca', fontSize: 13, color: '#7f1d1d', lineHeight: 1.55 }}>
          {insight}
        </div>
      )}

      {/* Big red CTA button — exactly as in Screenshot_74 */}
      <div style={{ padding: '4px 12px 14px' }}>
        <button
          onClick={handleInsight}
          disabled={loading}
          style={{
            width: '100%', padding: '15px',
            background: loading ? '#ef9a9a' : '#e53935',
            color: '#fff', border: 'none', borderRadius: 12,
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: 15, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            boxShadow: loading ? 'none' : '0 3px 12px rgba(229,57,53,0.32)',
            transition: 'background 0.2s',
          }}
        >
          <span style={{ fontSize: 16 }}>✨</span>
          {loading ? 'Getting Insight…' : 'Get AI Insight'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const [activeNav, setActiveNav] = useState('Dashboard');

  return (
    <div style={{ minHeight: '100vh', background: '#f2f2f2', fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif" }}>
      <NavBar activeNav={activeNav} setActiveNav={setActiveNav} />

      <div style={{ padding: '24px 28px', maxWidth: 1500, margin: '0 auto' }}>
        {/* 3-column layout: Left 280px | Center flex-1 | Right 300px */}
        <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>

          {/* ── LEFT: store photo + coupons sidebar ── */}
          <div style={{ width: 280, flexShrink: 0 }}>
            <StoreCard />
            <CouponsSidebar />
          </div>

          {/* ── CENTER + RIGHT: stacked layout ── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Welcome row */}
            <div>
              <h1 style={{ margin: 0, fontSize: 34, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.5px' }}>
                Welcome in, <span style={{ fontWeight: 900 }}>Mannyo</span> !
              </h1>
              <div style={{ display: 'flex', gap: 28, marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 18 }}>🌸</span>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>14</span>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Total Followers</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 18 }}>👤</span>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>768</span>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Total Referrals</span>
                </div>
              </div>
            </div>

            {/* ── Feature banner: full width across center+right ── */}
            <FeatureBanner />

            {/* ── Below banner: chart (left) + Premium+Actions (right) ── */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

              {/* Chart column */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PointsDollarsChart />
                <PointRequestsTable />
              </div>

              {/* Right column: Premium card + Actions */}
              <div style={{ width: 400, flexShrink: 0 }}>
                <PremiumCard />
                <ActionsPanel />
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
